import { Dispatch } from "redux";
import { ThunkAction } from "redux-thunk";

import axios from "helpers/axiosInstance";
import { composeApiURL } from "helpers/utility/axiosHelpers";

import { State } from "redux/store";
import {
  HOVERED_FILE_STATE,
  IS_CARDSLIST_LOADING,
  IS_CONTENT_LOADING,
  IS_FILE_FILTER_LOADING,
  IS_ISSUE_TYPE_LOADING,
  IS_LABEL_LOADING,
  IS_USER_TAG_LOADING,
  RESET_STORE,
  SET_ASSIGNED_TAGS,
  SET_AUTOCOMPLETE_LOADING,
  SET_AUTOCOMPLETE_NO_RESULTS_FLAG,
  SET_AUTOCOMPLETE_SUGGESTIONS,
  SET_CLUSTER_METADATA_SUMMARY,
  SET_CLUSTER_METADATA_SUMMARY_LOADING,
  SET_CLUSTER_PAGE_SCROLL_POSITION,
  SET_CONTEXTUAL_USER_TAGS,
  SET_DATA_METADATA_SUMMARY,
  SET_DATA_METADATA_SUMMARY_LOADING,
  SET_DATA_PAGE_SCROLL_POSITION,
  SET_DATASET_CONTEXTUAL_LABELS,
  SET_DATASET_ENRICHMENT_MODELS,
  SET_DATASET_EXTERNAL_SOURCES,
  SET_DATASET_ISSUE_TYPES,
  SET_DATASET_LABELS,
  SET_DATASET_SORTING_VALUE,
  SET_DATASET_TAGS,
  SET_DUPLICATE_THRESHOLD_RANGE,
  SET_ENTITY_TYPE_FILTER,
  SET_EXPLORATION_STATS,
  SET_EXPORT_DATA,
  SET_EXPORT_DATA_TIMEOUT_ID,
  SET_EXPORT_PROGRESS,
  SET_EXPORT_STATUS,
  SET_FETCH_ERROR,
  SET_FILE_NAME_SEARCH_TEXT,
  SET_FILTER_CONFIG,
  SET_FILTER_ISSUE,
  SET_GLOBAL_MD_TYPE,
  SET_IMAGE_PAGE_PARAMS_CONTEXT,
  SET_INVITED_EMAILS,
  SET_IS_IMAGE_UPLOADING,
  SET_IS_UNIQUENESS_SCORE_LOADING,
  SET_IS_USER_CONFIG_LOADING,
  SET_ISSUE_DATA,
  SET_LAST_SELECTED_INDEX,
  SET_METADATA_SOURCES_LOADING,
  SET_NAVIGATION_CLUSTER,
  SET_NAVIGATION_CLUSTER_LOADER,
  SET_NAVIGATION_CLUSTER_TAGS,
  SET_NAVIGATION_CONTEXT,
  SET_NO_RESULTS_FOUND,
  SET_PAGINATION_METADATA_FOR_CLUSTER,
  SET_PAGINATION_METADATA_FOR_DATA,
  SET_RECENT_SEARCHES,
  SET_REMOVED_ELEMENTS_BY_UNIQUENESS_COUNT,
  SET_SCROLL_APPLIED,
  SET_SELECTED_QUERY_MODAL,
  SET_SEVERITY_DATA,
  SET_SHOW_CART,
  SET_SHOW_DATASET_SHARING_MENU,
  SET_SHOW_EXPORT_MODAL,
  SET_SHOW_QUERY_PANEL,
  SET_SHOW_VIEW_SHARING_MENU,
  SET_SIMILARITY_DATA,
  SET_SIMILARITY_THRESHOLDS,
  SET_SIMILARITY_VERTEX,
  SET_TEMP_EXPORT_DATA,
  SET_TEMPORARY_ASSIGNED_TAGS,
  SET_TEMPORARY_EXPORT_DATA,
  SET_TREE_FOLDER,
  SET_USER_TAGS,
  SET_USERS_WITH_ACCESS,
  UPDATE_USER_TAGS,
} from "./constants";
import {
  DatasetExportData,
  DuplicatesThresholdRange,
  EntityType,
  ExportData,
  FilterOption,
  FiltersObject,
  isValidEntityType,
  MetadataItem,
  MetadataSource,
  MetadataType,
  QueryModalData,
  SingleExportItem,
} from "types";

import { isFastdupUser, transformFilterData } from "helpers/utility/utilities";
import {
  setShowDuplicatesRange,
  setShowMislabelsRange,
} from "redux/Metadata/actions";
import { addImageTag } from "redux/Modals/actions";
import {
  CLUSTER_DETAILS_PAGE,
  DATA_PAGE,
  IMAGE_DETAILS_PAGE,
} from "helpers/constants/pages";

import {
  getCurrentFilterQueries,
  getExportRequestData,
  getIsExplorationPerformanceEnabled,
  getNavigationCluster,
  getNavigationClusterID,
  getRawExportData,
  getRecentSearches,
  getSelectedCardTags,
  getSelectedQueryModal,
  getTemporaryExportData,
  getTemporaryExportDataArray,
  getVertexQuery,
} from "./selectors";
import { getEntityTypeValue } from "helpers/utility/queryUtils";
import { AxiosResponse, CancelTokenSource } from "axios";
import {
  ENTITY_TYPE_PARAMETER,
  ISSUE_CONFIDENCE,
  ISSUE_TYPE_PARAMETER,
  UNIQUENESS_SCORE_PARAMETER,
  VERTEX_ID,
  VQL_PARAMETER,
} from "helpers/constants/miscellaneous";
import { ExportFormatOptions, FilesToExport } from "views/modals/ExportModal";
import { isAnonymousUser, userLogout } from "redux/authReducer";
import { downloadFile } from "../../helpers/utility/downloadFile";

let cancelExploreToken: CancelTokenSource | undefined;

const FALLBACK_EXPORT_ERROR_TEXT =
  "Unfortunately, we encountered an error while processing the file. Please try again.";

export const setContentLoader = (data: any) => {
  return { type: IS_CONTENT_LOADING, data };
};

export const setCardslistLoading = (data: any) => {
  return { type: IS_CARDSLIST_LOADING, data };
};

export const setLabelLoading = (data: any) => {
  return { type: IS_LABEL_LOADING, data };
};

export const setIssueTypeLoading = (data: any) => {
  return { type: IS_ISSUE_TYPE_LOADING, data };
};

export const setFileFilterLoading = (data: any) => {
  return { type: IS_FILE_FILTER_LOADING, data };
};

export const setUniquenessScoreLoading = (data: any) => {
  return { type: SET_IS_UNIQUENESS_SCORE_LOADING, data };
};

export const setIsUserConfigLoading = (data: boolean) => {
  return { type: SET_IS_USER_CONFIG_LOADING, data };
};

export const setDatasetSortingValue = (data: any) => {
  return { type: SET_DATASET_SORTING_VALUE, data };
};

export const setIssueData = (data: any) => {
  return { type: SET_ISSUE_DATA, data };
};

export const setSeverityData = (data: any) => {
  return { type: SET_SEVERITY_DATA, data };
};

export const setFilterIssue = (data: any) => {
  return { type: SET_FILTER_ISSUE, data };
};

export const setSimilarityData = (data: any) => {
  return { type: SET_SIMILARITY_DATA, data };
};

export const setExplorationStats = (data: any) => {
  return { type: SET_EXPLORATION_STATS, data };
};

export const setSimilarityVertex = (data: any) => {
  return { type: SET_SIMILARITY_VERTEX, data };
};

export const setSimilarityThresholds = (data: {
  similarity_thresholds: string[];
}) => {
  return { type: SET_SIMILARITY_THRESHOLDS, data };
};

export const setDatasetLabels = (data: MetadataItem[]) => {
  return { type: SET_DATASET_LABELS, data };
};

export const setDatasetContextualLabels = (data: MetadataItem[]) => {
  return { type: SET_DATASET_CONTEXTUAL_LABELS, data };
};

export const setEntityTypeFilter = (data: MetadataItem[]) => {
  return { type: SET_ENTITY_TYPE_FILTER, data };
};

export const setDatasetIssueTypes = (data: MetadataItem[]) => {
  return { type: SET_DATASET_ISSUE_TYPES, data };
};

export const setNavigationCluster = (data: any) => {
  return { type: SET_NAVIGATION_CLUSTER, data };
};

export const setNavigationClusterTags = (data: any) => {
  return { type: SET_NAVIGATION_CLUSTER_TAGS, data };
};

export const setFileNameSearchText = (data: any) => {
  return { type: SET_FILE_NAME_SEARCH_TEXT, data };
};

export const setNavigationClusterLoader = (data: boolean) => {
  return { type: SET_NAVIGATION_CLUSTER_LOADER, data };
};

export const setFilterConfig = (data: any) => {
  return { type: SET_FILTER_CONFIG, data };
};

export const setTreeFolder = (data: any) => {
  return { type: SET_TREE_FOLDER, data };
};

export const setShowCart = (data: boolean) => {
  return { type: SET_SHOW_CART, data };
};

export const setShowExportModal = (data: string | null) => {
  return { type: SET_SHOW_EXPORT_MODAL, data };
};

export const setExportProgress = (data: number) => {
  return { type: SET_EXPORT_PROGRESS, data };
};

export const setExportStatus = (data: string | null) => {
  return { type: SET_EXPORT_STATUS, data };
};

export const setShowSharingMenu = (data: boolean) => {
  return { type: SET_SHOW_DATASET_SHARING_MENU, data };
};

export const setShowViewSharingMenu = (data: {
  showSharingMenu: boolean;
  associatedViewId: string | null;
}) => {
  return { type: SET_SHOW_VIEW_SHARING_MENU, data };
};

export const setUsersWithAccess = (data: any[]) => {
  return { type: SET_USERS_WITH_ACCESS, data };
};

export const setInvitedEmails = (data: string[]) => {
  return { type: SET_INVITED_EMAILS, data };
};

export const setLastSelectedIndex = (data: string | null) => {
  return { type: SET_LAST_SELECTED_INDEX, data };
};

export const setScrollApplied = (data: boolean) => {
  return { type: SET_SCROLL_APPLIED, data };
};

export const setDataPageScrollPosition = (data: string | null) => {
  return { type: SET_DATA_PAGE_SCROLL_POSITION, data };
};

export const setClusterPageScrollPosition = (data: string | null) => {
  return { type: SET_CLUSTER_PAGE_SCROLL_POSITION, data };
};

export const setNoResultsFound = (data: boolean) => {
  return { type: SET_NO_RESULTS_FOUND, data };
};

export const setDatasetEnrichmentModels = (data: MetadataSource[]) => {
  return { type: SET_DATASET_ENRICHMENT_MODELS, data };
};

export const setDuplicateThresholdRange = (
  data: DuplicatesThresholdRange | null,
) => {
  return { type: SET_DUPLICATE_THRESHOLD_RANGE, data };
};

export const setDatasetExternalSources = (data: MetadataSource[]) => {
  return { type: SET_DATASET_EXTERNAL_SOURCES, data };
};

export const setRemovedElementsByUniquenessCount = (data: number | null) => {
  return { type: SET_REMOVED_ELEMENTS_BY_UNIQUENESS_COUNT, data };
};

//Query Filter States
export const setSelectedQueryModal = (data: QueryModalData | null) => {
  return { type: SET_SELECTED_QUERY_MODAL, data };
};

export const updateSelectedQueryModal = (data: QueryModalData): any => {
  return async (dispatch: Dispatch, getState: () => State) => {
    const selectedQueryModal = getSelectedQueryModal(getState());
    dispatch(
      setSelectedQueryModal(
        selectedQueryModal ? { ...selectedQueryModal, ...data } : data,
      ),
    );
  };
};

export const setShowQueryPanel = (data: boolean) => {
  return { type: SET_SHOW_QUERY_PANEL, data };
};

export const setGlobalMDType = (data: MetadataType) => {
  return { type: SET_GLOBAL_MD_TYPE, data };
};

export const fetchDataset = (
  uuid: any,
  clusterId?: any,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    const existingDatasetId =
      getState().singleDataset?.stats?.dataset?.id || null;
    if (existingDatasetId !== uuid) {
      dispatch(setSeverityData(null));
    }
    dispatch(setContentLoader(true));
    if (!clusterId) {
      dispatch(setDatasetIssueTypes([]));
    }
    try {
      const statsUrl = composeApiURL(
        process.env.REACT_APP_API_ENDPOINT!,
        `/dataset/${uuid}/stats`,
      );
      const statsResponse = await axios.get(statsUrl);
      const stats = statsResponse.data;
      const redirectUrl = stats.redirect_url;
      if (redirectUrl) {
        window.location.href = redirectUrl;
      }
      if (
        !stats?.dataset?.sample &&
        isAnonymousUser() &&
        process.env.REACT_APP_IS_AUTHENTICATION_DISABELED === "false" &&
        !isFastdupUser()
      ) {
        userLogout();
      }
      dispatch(setSeverityData(stats));
      dispatch(setContentLoader(false));

      if (
        stats?.dataset?.objects_duplicate_thresholds ||
        stats?.dataset?.images_duplicate_thresholds
      ) {
        const { objects_duplicate_thresholds, images_duplicate_thresholds } =
          stats.dataset;

        dispatch(
          setDuplicateThresholdRange({
            objectsDuplicateThresholds: objects_duplicate_thresholds || [],
            imagesDuplicateThresholds: images_duplicate_thresholds || [],
          }),
        );
      } else {
        dispatch(setDuplicateThresholdRange(null));
      }

      if (!clusterId) dispatch(setIssueTypeLoading(false));
    } catch (error) {
      dispatch(setContentLoader(false));
      if (!clusterId) dispatch(setIssueTypeLoading(false));
      dispatch(setFetchError(true));
      dispatch(setSeverityData(null));
      console.log(error);
    }
  };
};

export const fetchDatasetIssues = (
  uuid: any,
  queryParamsFilters: FiltersObject,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    try {
      dispatch(setIssueTypeLoading(true));
      const queryParams = getCurrentFilterQueries(
        getState(),
        queryParamsFilters,
        false,
        true,
      );
      const issuesUrl = composeApiURL(
        process.env.REACT_APP_API_ENDPOINT!,
        `/dataset/${uuid}/issue_types_of_entities${queryParams}`,
      );
      const issuesReponse = await axios.get(issuesUrl);
      const issueTypes = issuesReponse.data.map((issue: any) => {
        return {
          id: issue.id,
          text: issue.name,
        };
      });
      dispatch(setDatasetIssueTypes(issueTypes));
      dispatch(setIssueTypeLoading(false));
    } catch (error) {
      dispatch(setIssueTypeLoading(false));
    }
  };
};
export const fetchClusterPageIssueTypes = (
  datasetId: any,
  clusterId: any,
  queryParamsFilters: FiltersObject,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    const state = { ...getState().singleDataset };
    dispatch(setIssueTypeLoading(true));

    const entityTypeValues = [...state.entityTypeFilter];

    let paramValue = "";
    if (entityTypeValues) {
      paramValue = getEntityTypeValue(
        entityTypeValues,
        isValidEntityType(queryParamsFilters[ENTITY_TYPE_PARAMETER])
          ? queryParamsFilters[ENTITY_TYPE_PARAMETER]
          : null,
      );
    }
    try {
      const path = `/dataset/${datasetId}/cluster/${clusterId}/issues?entity_type=${paramValue}`;

      const url = composeApiURL(process.env.REACT_APP_API_ENDPOINT!, path);

      const response = await axios.get(url);

      if (response.data && response.data.length > 0) {
        const issueTypes = response.data.map((issue: any) => {
          return {
            id: issue.id,
            text: issue.name,
          };
        });
        dispatch(setDatasetIssueTypes(issueTypes));
        dispatch(setIssueTypeLoading(false));
      }
      dispatch(setIssueTypeLoading(false));
    } catch (error) {
      dispatch(setIssueTypeLoading(false));
      console.log(error);
    }
  };
};

export const fetchSortedData = (
  uuid: any,
  sortingValue: any,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(setIssueData(null));
      dispatch(setCardslistLoading(true));
      const url = composeApiURL(
        process.env.REACT_APP_API_ENDPOINT!,
        `/dataset/${uuid}/issues${
          sortingValue === "name" ? "sort_by=name" : null
        }`,
      );

      const response = await axios.get(url);

      dispatch(setIssueData(response.data));
    } catch (error) {
      dispatch(setCardslistLoading(false));
      console.log(error);
    }
  };
};

export const updateUserTagsWithNewTags = (data: MetadataItem[]) => {
  return { type: UPDATE_USER_TAGS, data };
};

export const setUserTags = (data: MetadataItem[]) => {
  return { type: SET_USER_TAGS, data };
};

export const setContextualUserTags = (data: any) => {
  return { type: SET_CONTEXTUAL_USER_TAGS, data };
};

export const setDatasetTags = (data: any) => {
  return { type: SET_DATASET_TAGS, data };
};

const checkForUniqueScore = (filters: FiltersObject) => {
  if (!!filters[UNIQUENESS_SCORE_PARAMETER]) return true;
  if (!!filters[VQL_PARAMETER]) {
    const queries = JSON.parse(filters[VQL_PARAMETER]);
    return queries.some((q: any) => q.uniqueness?.op === "uniqueness");
  }
  return false;
};

const handleUniquenessCheck = async (
  url: string,
  dispatch: Dispatch,
  responseData: any,
  filters: FiltersObject,
): Promise<void> => {
  const uniquenessTotal = responseData?.totalElements || null;
  const urlObj = new URL(url, window.location.href);

  urlObj.searchParams.delete(UNIQUENESS_SCORE_PARAMETER);

  // Check if filters.vql exists and contains the uniqueness filter
  if (filters.vql?.includes('"op":"uniqueness"')) {
    const vqlQueries = JSON.parse(filters.vql);

    const filteredVqlQueries = vqlQueries.filter(
      (query: any) => query.uniqueness?.op !== "uniqueness",
    );

    const filteredVql = JSON.stringify(filteredVqlQueries);
    urlObj.searchParams.set("vql", filteredVql);
  }

  const responseWithoutUniqueness = await axios.get(urlObj.toString());

  const actualTotal = responseWithoutUniqueness.data?.totalElements || null;

  const removedElements =
    uniquenessTotal && actualTotal
      ? Math.abs(uniquenessTotal - actualTotal)
      : null;

  dispatch(setRemovedElementsByUniquenessCount(removedElements));
};

export const fetchDataMetadataSummary = (
  datasetId: string,
  queryParamsFilters: FiltersObject,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    const queries = getCurrentFilterQueries(getState(), queryParamsFilters);

    try {
      dispatch(dataMetadataSummaryLoading(true));
      const url = composeApiURL(
        process.env.REACT_APP_API_ENDPOINT!,
        `/explore/${datasetId}/metadata_summary${queries}`,
      );
      try {
        const response = await axios.get(url);

        dispatch(setDataMetadataSummary(response.data));

        if (checkForUniqueScore(queryParamsFilters)) {
          await handleUniquenessCheck(
            url,
            dispatch,
            response.data,
            queryParamsFilters,
          );
        }
        dispatch(setUniquenessScoreLoading(false));
        dispatch(dataMetadataSummaryLoading(false));
      } catch (error) {
        console.log(error);
        dispatch(dataMetadataSummaryLoading(false));
        dispatch(setUniquenessScoreLoading(false));
      }
    } catch (e) {
      console.log("Error fetching Metadata Summary for Data Page", e);
      dispatch(dataMetadataSummaryLoading(false));
      dispatch(setUniquenessScoreLoading(false));
    }
  };
};

export const fetchClusterMetadataSummary = (
  datasetId: string,
  clusterId: string,
  queryParamsFilters: FiltersObject,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    dispatch(clusterMetadataSummaryLoading(true));
    const queries = getCurrentFilterQueries(getState(), queryParamsFilters);
    const url = composeApiURL(
      process.env.REACT_APP_API_ENDPOINT!,
      `/explore/${datasetId}/similarity_cluster/${clusterId}/metadata_summary${queries}`,
    );
    try {
      const response = await axios.get(url);
      dispatch(setClusterMetadataSummary(response.data));
      if (checkForUniqueScore(queryParamsFilters)) {
        await handleUniquenessCheck(
          url,
          dispatch,
          response.data,
          queryParamsFilters,
        );
      }
      dispatch(setUniquenessScoreLoading(false));
    } catch (error) {
      console.log(error);
      dispatch(setUniquenessScoreLoading(false));
    }
    dispatch(clusterMetadataSummaryLoading(false));
  };
};

export const fetchNavigationClusterTags = (
  datasetId: string,
  clusterId: string,
  queryParamsFilters: FiltersObject,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    dispatch(setNavigationClusterTags(null));
    const queries = getCurrentFilterQueries(getState(), queryParamsFilters);
    dispatch(clusterMetadataSummaryLoading(true));
    const url = composeApiURL(
      process.env.REACT_APP_API_ENDPOINT!,
      `/explore/${datasetId}/similarity_cluster/${clusterId}/tags${queries}`,
    );
    try {
      const response = await axios.get(url);
      dispatch(setNavigationClusterTags(response.data));
    } catch (error) {
      console.log(error);
    } finally {
      dispatch(clusterMetadataSummaryLoading(false));
    }
  };
};

let lastClusterQueries = "";

export const fetchNavigationCluster = (
  datasetId: string,
  clusterId: string,
  queryParamsFilters: FiltersObject,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    dispatch(setNavigationClusterLoader(true));
    dispatch(setCardslistLoading(true));
    const vertexID = queryParamsFilters[VERTEX_ID];
    let vertexDataPromise;
    if (vertexID) {
      vertexDataPromise = dispatch(
        fetchVertexData(datasetId, queryParamsFilters) as any,
      );
    }

    const basePath = `/explore/${datasetId}/similarity_cluster/${clusterId}`;
    const queries = getCurrentFilterQueries(getState(), queryParamsFilters);
    if (queries !== lastClusterQueries) {
      dispatch(setClusterPageScrollPosition(null));
    }
    lastClusterQueries = queries;
    const path = `${basePath}${queries}`;
    try {
      const url = composeApiURL(process.env.REACT_APP_API_ENDPOINT!, path);
      const similarityClusterPromise = axios.get(url);
      Promise.all([vertexDataPromise, similarityClusterPromise]).then(
        (values) => {
          const vertexResponse = values[0];
          const similarityResponse = values[1];
          const similarityData = similarityResponse.data;
          const hasNoResults =
            similarityData?.previews?.length < (!!vertexResponse ? 2 : 1) || //response includes the vertex preview if vertex is selected
            false;

          similarityData?.previews.forEach((p: any) => {
            p.__id = p.media_id;
          });

          dispatch(setNoResultsFound(hasNoResults));
          dispatch(setNavigationCluster(similarityData));
          if (
            !similarityData ||
            similarityData?.length <= 0 ||
            (vertexResponse && similarityData.length <= 1)
          ) {
            dispatch(setNavigationClusterLoader(false));
            if (!similarityData) dispatch(setCardslistLoading(false));
          }
        },
      );
    } catch (error) {
      console.log(error);
      dispatch(setFetchError(true));
      dispatch(setNavigationClusterLoader(false));
      dispatch(setCardslistLoading(false));
      return;
    }

    dispatch(
      fetchClusterMetadataSummary(
        datasetId,
        clusterId,
        queryParamsFilters,
      ) as any,
    );
    dispatch(
      fetchNavigationClusterTags(
        datasetId,
        clusterId,
        queryParamsFilters,
      ) as any,
    );
  };
};

export const fetchVertexData = (
  datasetId: any,
  queryParamsFilters: FiltersObject,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch) => {
    const vertexID = queryParamsFilters[VERTEX_ID];
    if (!vertexID) {
      return;
    }
    const basePath = `/explore/${datasetId}/media/${vertexID}/vertex`;
    const queries = getVertexQuery(queryParamsFilters) || "";
    const path = `${basePath}${queries}`;
    const url = composeApiURL(process.env.REACT_APP_API_ENDPOINT!, path);
    dispatch(setSimilarityVertex(null));
    const response = await axios.get(url);
    dispatch(setSimilarityVertex(response.data));
    return response;
  };
};

let lastSimilarityQueries = "";

export const fetchSimilarityData = (
  datasetId: any,
  queryParamsFilters: FiltersObject,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    if (cancelExploreToken) {
      cancelExploreToken.cancel(); // Cancel the previous request if it's still pending
    }
    dispatch(setClusterMetadataSummary(null));
    dispatch(setSimilarityData(null));
    dispatch(setCardslistLoading(true));
    const isExplorationPerformanceEnabled =
      getIsExplorationPerformanceEnabled(getState());
    const state = getState().singleDataset;
    const vertexID = queryParamsFilters[VERTEX_ID];
    let vertexDataPromise;
    if (vertexID) {
      vertexDataPromise = dispatch(
        fetchVertexData(datasetId, queryParamsFilters) as any,
      );
    }

    const basePath = `/explore/${datasetId}`;
    const queries = getCurrentFilterQueries(getState(), queryParamsFilters);
    if (queries !== lastSimilarityQueries) {
      dispatch(setDataPageScrollPosition(null));
    }
    lastSimilarityQueries = queries;
    const path = `${basePath}${queries}`;
    try {
      const url = composeApiURL(process.env.REACT_APP_API_ENDPOINT!, path);
      cancelExploreToken = axios.CancelToken.source();
      const similarityDataPromise = axios.get(url, {
        cancelToken: cancelExploreToken.token,
      });
      await Promise.all([vertexDataPromise, similarityDataPromise]).then(
        (values) => {
          const vertexResponse = values[0];

          const similarityResponse = values[1];
          if (!similarityResponse) {
            // Request was probably cancelled, there should be a follow up request about to resolve.
            return;
          }
          similarityResponse.data.clusters.forEach((c: any) => {
            if (queryParamsFilters.flat_response) {
              c.flat_response = true;
              c.__id = c.previews[0].media_id;
            } else {
              c.__id = c.cluster_id;
            }
          });

          const hasNoResults =
            similarityResponse.data?.clusters?.length < 1 || false;
          dispatch(setNoResultsFound(hasNoResults));

          dispatch(setSimilarityData(similarityResponse.data.clusters));

          if (
            similarityResponse.data?.clusters?.length <= 0 ||
            (vertexResponse && similarityResponse.data?.clusters?.length <= 1)
          ) {
            dispatch(setCardslistLoading(false));
          }
        },
      );
    } catch (error: any) {
      console.log(error);
      dispatch(setFetchError(true));
      dispatch(setCardslistLoading(false));
    }

    // Fetch exploration stats in a non-blocking async request
    if (
      state.filterConfig?.find(
        (feature: any) => feature.feature_key === "exploration_stats",
      )?.feature_behavior === "SHOW"
    ) {
      if (!!vertexID === false) {
        try {
          const statsPath = path.replace("/explore", "/exploration_stats");
          const statsUrl = composeApiURL(
            process.env.REACT_APP_API_ENDPOINT!,
            statsPath,
          );
          const response = await axios.get(statsUrl);
          dispatch(setExplorationStats(response.data));
        } catch (error) {
          console.log(error);
        }
      }
    }

    // Fetch metadata for dataset (right-pane) in a non-blocking async request
    // if filterConfig cluster_metadata_summary is enabled, fetch cluster metadata summary
    //change condition to check isFeatureEnabled when this needs to be gated
    if (!isExplorationPerformanceEnabled) {
      dispatch(fetchDataMetadataSummary(datasetId, queryParamsFilters) as any);
    }
  };
};

export const searchImageFileUploaded = (
  datasetId: any,
  file: File,
  queryParamsFilters: FiltersObject,
  croppedFile?: File,
  boundingBoxValues?: string,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    const entitityTypeFilter = [...getState().singleDataset.entityTypeFilter];

    var bodyFormData = new FormData();
    bodyFormData.append("file", file);
    const entityQueryText = getEntityTypeValue(
      entitityTypeFilter,
      isValidEntityType(queryParamsFilters[ENTITY_TYPE_PARAMETER])
        ? queryParamsFilters[ENTITY_TYPE_PARAMETER]
        : null,
    );

    let queries = "";

    // Check if entityQueryText is not empty
    if (entityQueryText) queries = `?entity_type=${entityQueryText}`;
    if (boundingBoxValues) {
      // handle '&'and '?' prefix
      queries += `${queries ? "&" : "?"}bounding_box=${boundingBoxValues}`;
    }

    try {
      const path = `/dataset/${datasetId}/search-image-similarity${queries}`;
      const url = composeApiURL(process.env.REACT_APP_API_ENDPOINT!, path);
      const response = await axios.post(url, bodyFormData);
      return response.data.anchor_media_id;
    } catch (error) {
      console.log(error);
      dispatch(setCardslistLoading(false));
      return Promise.reject(error);
    }
  };
};

export const fetchDatasetLabels = (
  datasetId: any,
  entityType: EntityType = "IMAGES",
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch) => {
    const isObjectEntitySelected = entityType === "OBJECTS";
    try {
      dispatch(setLabelLoading(true));
      let path: any;
      path = `/explore/${datasetId}/labels?entity_type=${entityType}`;

      const url = composeApiURL(process.env.REACT_APP_API_ENDPOINT!, path);

      const response = await axios.get(url);
      let labels = response.data.labels || [];

      if (!!labels && labels.length > 0) {
        const indexOfUnlabeled = labels.findIndex(
          (label: string) => "unlabeled" === label.toLowerCase(),
        );
        if (indexOfUnlabeled !== -1) {
          const unlabeledLabel = labels.splice(indexOfUnlabeled, 1)[0];
          if (!isObjectEntitySelected) {
            labels.unshift(unlabeledLabel);
          }
        }
        const datasetLabelsWithUnLabeledOnTop = labels.map(
          (label: any, index: number) => {
            return {
              id: index,
              text: label,
            };
          },
        );
        dispatch(setDatasetLabels(datasetLabelsWithUnLabeledOnTop));
      }
      dispatch(setLabelLoading(false));
    } catch (error) {
      console.log(error);
      dispatch(setLabelLoading(false));
    }
  };
};

export const fetchDatasetContextualLabels = (
  datasetId: any,
  queryParamsFilters: FiltersObject,
  clusterId?: any,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    const state = { ...getState().singleDataset };
    const entityTypeValue = getEntityTypeValue(
      state.entityTypeFilter,
      isValidEntityType(queryParamsFilters[ENTITY_TYPE_PARAMETER])
        ? queryParamsFilters[ENTITY_TYPE_PARAMETER]
        : "IMAGES",
    );
    const queryParams = getCurrentFilterQueries(
      getState(),
      queryParamsFilters,
      false,
      true,
    );
    const isObjectEntitySelected = entityTypeValue.includes("OBJECTS");

    try {
      dispatch(setLabelLoading(true));
      let path: any;
      if (clusterId) {
        path = `/explore/${datasetId}/cluster/${clusterId}/labels${queryParams}`;
      } else {
        path = `/explore/${datasetId}/labels${queryParams}`;
      }

      const url = composeApiURL(process.env.REACT_APP_API_ENDPOINT!, path);

      const response = await axios.get<{
        labels: Array<{ label: string; source: string }>;
      }>(url);
      let labels = response.data.labels || [];

      if (!!labels && labels.length > 0) {
        const indexOfUnlabeled = labels.findIndex(
          ({ label }) => "unlabeled" === label.toLowerCase(),
        );
        if (indexOfUnlabeled !== -1) {
          const unlabeledLabel = labels.splice(indexOfUnlabeled, 1)[0];
          if (!isObjectEntitySelected) {
            labels.unshift(unlabeledLabel);
          }
        }
        const datasetLabelsWithUnLabeledOnTop = labels.map(
          (label: any, index: number) => {
            return {
              id: index,
              text: label.label,
              source: label.source,
            };
          },
        );
        dispatch(setDatasetLabels(datasetLabelsWithUnLabeledOnTop));
        dispatch(setDatasetContextualLabels(datasetLabelsWithUnLabeledOnTop));
      }
      dispatch(setLabelLoading(false));
    } catch (error) {
      console.log(error);
      dispatch(setLabelLoading(false));
    }
  };
};

export const clearFilters =
  (): ThunkAction<void, State, unknown, any> =>
  async (dispatch: Dispatch, getState) => {
    const singleDatasetState = { ...getState().singleDataset };

    const clearOptions = (options: FilterOption[]) =>
      options.map((option) => ({ ...option, selected: false }));

    const clearAndDispatch = (
      action: (options: FilterOption[]) => void,
      options: FilterOption[],
    ) => {
      dispatch(action(clearOptions(options)) as any);
    };

    clearAndDispatch(setUserTags, singleDatasetState.userTags);
    clearAndDispatch(setDatasetIssueTypes, singleDatasetState.issueTypes);

    dispatch(setFileNameSearchText(""));
    dispatch(setNavigationCluster(null));
    dispatch(setScrollApplied(false));
    dispatch(setShowMislabelsRange(false));
    dispatch(setShowDuplicatesRange(false));
  };

export const fetchFilterConfig = (
  datasetId: any,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch) => {
    dispatch(setIsUserConfigLoading(true));
    const basePath = "/user_config";
    try {
      const url = composeApiURL(process.env.REACT_APP_API_ENDPOINT!, basePath);
      const response = await axios.get(url, {
        params: { dataset_id: datasetId },
      });
      const transformedData = transformFilterData(response.data?.features);

      dispatch(setFilterConfig(transformedData));
      dispatch(setIsUserConfigLoading(false));
      return { filterConfig: transformedData };
    } catch (error) {
      console.log(error);
      dispatch(setFetchError(true));
      dispatch(setCardslistLoading(false));
      dispatch(setIsUserConfigLoading(false));
      dispatch(setNavigationClusterLoader(false));
      dispatch(dataMetadataSummaryLoading(false));
      dispatch(clusterMetadataSummaryLoading(false));
    }
  };
};

export const fetchTreeFolder = (
  datasetId: any,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch) => {
    dispatch(setFileFilterLoading(true));
    const basePath = `/explore/${datasetId}/file_tree`;

    try {
      const url = composeApiURL(process.env.REACT_APP_API_ENDPOINT!, basePath);
      const response = await axios.get(url);
      dispatch(setTreeFolder(response.data));
      dispatch(setFileFilterLoading(false));
    } catch (error) {
      console.log(error);
      dispatch(setFileFilterLoading(false));
    }
  };
};

export const clearExportData = (
  datasetID: string,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    try {
      const exportData = getRawExportData(getState());
      if (exportData) {
        let newExportData = { ...exportData };

        if (datasetID in exportData) {
          delete newExportData[datasetID];
          dispatch(setRawExportData(newExportData));
          localStorage.setItem("cart__data", JSON.stringify(newExportData));
        }
      }
    } catch (error) {
      console.error(error);
    }
  };
};

export const clearSelectedMedia = (): ThunkAction<
  void,
  State,
  unknown,
  any
> => {
  return async (dispatch: Dispatch, getState) => {
    try {
      const state = getState();
      const exportData = getTemporaryExportData(state);
      const navigationCluster = getNavigationCluster(state);

      const currentData = exportData || { exportDataArray: [], tags: [] };

      const excludedMediaIds = new Set(
        navigationCluster?.previews.map((p: any) => p.media_id),
      );

      const filteredExportData = currentData.exportDataArray.filter(
        (item) => !excludedMediaIds.has(item.media_id),
      );

      dispatch(
        setTemporaryExportData({
          exportDataArray: filteredExportData,
          tags: currentData.tags,
        }),
      );
    } catch (error) {
      console.error("Failed to clear selected media IDs:", error);
    }
  };
};

export const setRawExportData = (data: ExportData | null) => {
  return { type: SET_EXPORT_DATA, data };
};

export const setTemporaryExportData = (data: DatasetExportData | null) => {
  return { type: SET_TEMPORARY_EXPORT_DATA, data };
};

export const setTempExportData = (data: any) => {
  return { type: SET_TEMP_EXPORT_DATA, data };
};

export const setExportDataTimeout = (data: any) => {
  return { type: SET_EXPORT_DATA_TIMEOUT_ID, data };
};

export const setClusterMetadataSummary = (data: any) => {
  return { type: SET_CLUSTER_METADATA_SUMMARY, data };
};

export const setDataMetadataSummary = (data: any) => {
  return { type: SET_DATA_METADATA_SUMMARY, data };
};

export const clusterMetadataSummaryLoading = (data: any) => {
  return { type: SET_CLUSTER_METADATA_SUMMARY_LOADING, data };
};

export const dataMetadataSummaryLoading = (data: boolean) => {
  return { type: SET_DATA_METADATA_SUMMARY_LOADING, data };
};

export const setMetadataSourcesLoading = (data: any) => {
  return { type: SET_METADATA_SOURCES_LOADING, data };
};

const setUserTagLoading = (data: boolean) => {
  return { type: IS_USER_TAG_LOADING, data };
};

const handleClusterUpdate = (
  exportDataArray: SingleExportItem[],
  selectedData: any,
  type: "cluster" | "media",
) => {
  const existingCluster = exportDataArray.find(
    (data) => data.cluster_id === selectedData.cluster_id,
  );

  if (existingCluster) {
    return exportDataArray.filter(
      (data) => data.cluster_id !== selectedData.cluster_id,
    );
  } else {
    return [...exportDataArray, { ...selectedData, exportType: type }];
  }
};

const handleMediaUpdate = (
  exportDataArray: SingleExportItem[],
  selectedData: any,
  type: "cluster" | "media",
) => {
  const existingParentCluster = exportDataArray.find(
    (data) =>
      data.cluster_id === selectedData.cluster_id &&
      data.exportType === "cluster",
  );

  if (existingParentCluster) {
    return updateClusterExcludeMedia(
      exportDataArray,
      existingParentCluster,
      selectedData.media_id,
    );
  } else {
    return updateSingleMedia(exportDataArray, selectedData, type);
  }
};

const updateClusterExcludeMedia = (
  exportDataArray: SingleExportItem[],
  parentCluster: any,
  selectedDataMediaId: any,
) => {
  const excludeMediaIDs = [...(parentCluster.excludeMediaIDs || [])];
  const mediaIndex = excludeMediaIDs.indexOf(selectedDataMediaId);

  if (mediaIndex > -1) {
    excludeMediaIDs.splice(mediaIndex, 1);
  } else {
    excludeMediaIDs.push(selectedDataMediaId);
  }

  return exportDataArray.map((data) =>
    data.cluster_id === parentCluster.cluster_id
      ? { ...parentCluster, excludeMediaIDs }
      : data,
  );
};

const updateSingleMedia = (
  exportDataArray: SingleExportItem[],
  selectedData: any,
  type: "cluster" | "media",
) => {
  const existingMedia = exportDataArray.find(
    (data) => data.media_id === selectedData.media_id,
  );

  if (existingMedia) {
    return exportDataArray.filter(
      (data) => data.media_id !== selectedData.media_id,
    );
  } else {
    return [
      ...exportDataArray,
      { ...selectedData, exportType: type, variant: "temporary" },
    ];
  }
};

export const updateExportData = (
  datasetID: string,
  selectedExportData: any,
  type: "cluster" | "media",
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    try {
      const state = getState();
      const exportData = getRawExportData(state);
      const currentData = exportData?.[datasetID] || {
        exportDataArray: [],
        tags: [],
      };

      let newExportDataArray: SingleExportItem[] = currentData.exportDataArray;

      if (type === "cluster") {
        newExportDataArray = handleClusterUpdate(
          newExportDataArray,
          selectedExportData,
          type,
        );
      } else if (type === "media") {
        newExportDataArray = handleMediaUpdate(
          newExportDataArray,
          selectedExportData,
          type,
        );
      }

      const newData = {
        [datasetID]: {
          exportDataArray: newExportDataArray,
          tags: currentData.tags,
        },
      };
      const newExportData = { ...exportData, ...newData };

      dispatch(setRawExportData(newExportData));
      localStorage.setItem("cart__data", JSON.stringify(newData));
    } catch (error) {
      console.error(error);
    }
  };
};

export const updateTemporaryExportData = (
  selectedData: any,
  type: "cluster" | "media",
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    try {
      const state = getState();
      const temporaryData = getTemporaryExportData(state);
      const currentData: DatasetExportData = temporaryData || {
        exportDataArray: [],
        tags: [],
      };

      let newExportDataArray: SingleExportItem[] = currentData.exportDataArray;

      if (type === "cluster") {
        newExportDataArray = handleClusterUpdate(
          newExportDataArray,
          selectedData,
          type,
        );
      } else if (type === "media") {
        newExportDataArray = handleMediaUpdate(
          newExportDataArray,
          selectedData,
          type,
        );
      }

      const newData = {
        exportDataArray: newExportDataArray,
        tags: currentData.tags,
      };

      dispatch(setTemporaryExportData(newData));
    } catch (error) {
      console.error(error);
    }
  };
};

export const updateExportTags = (
  datasetID: string,
  tag: any,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    try {
      const exportData = { ...getState().singleDataset.exportData };
      const exportDataArray = exportData[datasetID]?.exportDataArray || [];
      const newExportData = { ...exportData };

      if (datasetID in exportData) {
        const tagsArray = exportData[datasetID]?.tags || [];

        const exists = tagsArray.some(
          (singleTag: any) => singleTag.id === tag.id,
        );

        const newTagsArray = exists
          ? tagsArray
          : [...tagsArray, { ...tag, selected: true }];

        const newEntry = {
          [datasetID]: { tags: newTagsArray, exportDataArray: exportDataArray },
        };

        newExportData[datasetID] = {
          ...newExportData[datasetID],
          ...newEntry[datasetID],
        };
      } else {
        const newEntry = {
          [datasetID]: { tags: [tag], exportDataArray: exportDataArray },
        };

        newExportData[datasetID] = {
          ...newExportData[datasetID],
          ...newEntry[datasetID],
        };
      }

      dispatch(setRawExportData(newExportData));
      localStorage.setItem("cart__data", JSON.stringify(newExportData));
    } catch (error) {
      console.error(error);
    }
  };
};

export const updateTemporaryExportTags = (
  tag: FilterOption,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    try {
      const state = getState();
      const temporaryData = getTemporaryExportData(state);
      const currentData = temporaryData || { exportDataArray: [], tags: [] };

      const tagsArray = currentData.tags;
      const exists = tagsArray.some(
        (singleTag: any) => singleTag.id === tag.id,
      );

      const newTagsArray = exists
        ? tagsArray
        : [...tagsArray, { ...tag, selected: true }];

      const newData = {
        exportDataArray: currentData.exportDataArray,
        tags: newTagsArray,
      };

      dispatch(setTemporaryExportData(newData));
    } catch (error) {
      console.error(error);
    }
  };
};

export const addAllForExport = (
  datasetID: string,
  clusters: any[],
  allSelectedFilters: any,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    try {
      const exportData = { ...getState().singleDataset.exportData };
      const selectedClusters = clusters.map((cluster) => ({
        ...cluster,
        exportType: "cluster",
        cluster_filters: allSelectedFilters,
      }));

      let newExportData = { ...exportData };
      if (datasetID in exportData) {
        const existingClusters = exportData[datasetID].exportDataArray || [];

        // Merge new clusters with the existing clusters (avoiding duplicates based on ID)
        const updatedClusterArray = [
          ...existingClusters.filter(
            (existingCluster: { cluster_id: string }) => {
              return !selectedClusters.some((newCluster) => {
                return newCluster.cluster_id === existingCluster.cluster_id;
              });
            },
          ),
          ...selectedClusters,
        ];

        newExportData[datasetID] = {
          ...newExportData[datasetID],
          exportDataArray: updatedClusterArray,
        };
      } else {
        const tagsArray: any[] = [];
        const newEntry = {
          [datasetID]: { tags: tagsArray, exportDataArray: selectedClusters },
        };
        newExportData = { ...newExportData, ...newEntry };
      }
      dispatch(setRawExportData(newExportData));
      localStorage.setItem("cart__data", JSON.stringify(newExportData));
    } catch (error) {
      console.error("Error in adding clusters for export:", error);
    }
  };
};

export const addAllMediaIDsFromClusterPage = (
  datasetID: string,
  allSelectedFilters: any,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    const exportData = { ...getState().singleDataset.exportData };
    const navigationCluster = { ...getState().singleDataset.navigationCluster };
    const newExportData = { ...exportData };
    try {
      const exportDataArray = exportData[datasetID]?.exportDataArray || [];
      const tagsArray = exportData[datasetID]?.tags || [];
      let newExportDataArray: SingleExportItem[] = [];
      const mediaArray = navigationCluster.previews.map(
        (singlePreview: any) => {
          return {
            ...singlePreview,
            exportType: "media",
            cluster_id: navigationCluster.cluster_id,
            cluster_filters: allSelectedFilters,
          };
        },
      );
      //add all single entities as media
      newExportDataArray = [...exportDataArray, ...mediaArray];

      const newEntry = {
        [datasetID]: { exportDataArray: newExportDataArray, tags: tagsArray },
      };

      newExportData[datasetID] = {
        ...newExportData[datasetID],
        ...newEntry[datasetID],
      };

      dispatch(setRawExportData(newExportData));
      localStorage.setItem("cart__data", JSON.stringify(newExportData));
    } catch (error) {
      console.error("Error clearing selections", error);
    }
  };
};

export const saveTemporarySelection = (
  datasetID: string,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    try {
      const state = getState();
      const exportData = getRawExportData(state);
      const temporaryExportDataArray = getTemporaryExportDataArray(state);

      if (temporaryExportDataArray.length > 0) {
        const currentDatasetExportData = exportData?.[datasetID] || {
          exportDataArray: [],
          tags: [],
        };

        const existingIds = new Set(
          currentDatasetExportData.exportDataArray.map(
            (item) => item.cluster_id,
          ),
        );

        const filteredTempArray = temporaryExportDataArray.filter(
          (item) => !existingIds.has(item.cluster_id),
        );

        const newExportDataArray = [
          ...currentDatasetExportData.exportDataArray,
          ...filteredTempArray,
        ];

        const newEntry = {
          [datasetID]: {
            exportDataArray: newExportDataArray,
            tags: currentDatasetExportData.tags,
          },
        };

        const newExportData = { ...exportData, ...newEntry };

        dispatch(setRawExportData(newExportData));
        dispatch(setTemporaryExportData(null));
        localStorage.setItem("cart__data", JSON.stringify(newExportData));
      }
    } catch (error) {
      console.error(error);
    }
  };
};

export const fetchCardDataOnTagUpdate = (
  datasetID: string,
  queryParamsFilters: FiltersObject,
  clusterID?: string,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch) => {
    if (clusterID) {
      await dispatch(
        fetchNavigationCluster(datasetID, clusterID, queryParamsFilters) as any,
      );
      dispatch(setNavigationClusterLoader(false));
      dispatch(setCardslistLoading(false));
    } else {
      dispatch(fetchSimilarityData(datasetID, queryParamsFilters) as any);
    }
    dispatch(fetchUserTags(datasetID, queryParamsFilters, clusterID) as any);
  };
};

export const submitSelectedCartTags = (
  datasetID: string,
  requestBody: any,
  queryParamsFilters: FiltersObject,
  clusterID?: string,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch) => {
    return new Promise<void>(async (resolve, reject) => {
      try {
        const path = `/dataset/${datasetID}/tags`;
        const url = composeApiURL(process.env.REACT_APP_API_ENDPOINT!, path);
        axios({
          url,
          method: "post",
          data: { tags_assignment: requestBody },
        })
          .then(() => {
            dispatch(
              fetchCardDataOnTagUpdate(
                datasetID,
                queryParamsFilters,
                clusterID,
              ) as any,
            );

            resolve();
          })
          .catch((error) => {
            reject(error);
          });
      } catch (error) {
        console.log(error);
        reject(error);
      }
    });
  };
};

const fetchExportStatus = (
  taskID: string,
  datasetID: string,
): ThunkAction<Promise<string>, State, unknown, any> => {
  return async (dispatch: Dispatch) => {
    const url = composeApiURL(
      process.env.REACT_APP_API_ENDPOINT!,
      `/dataset/${datasetID}/export_status?export_task_id=${taskID}`,
    );

    const checkStatus = async (): Promise<any> => {
      try {
        const response = await axios.get(url);
        const responseData = response.data;

        if (!responseData) {
          throw new Error(FALLBACK_EXPORT_ERROR_TEXT);
        } else if (
          responseData.status === "REJECTED" ||
          responseData.status === "FAILED"
        ) {
          dispatch(setExportProgress(0));
          dispatch(setExportStatus(null));
          throw new Error(
            responseData.result_message || FALLBACK_EXPORT_ERROR_TEXT,
          );
        } else if (responseData.download_uri) {
          dispatch(setExportStatus(null));
          return responseData.download_uri;
        } else {
          dispatch(setExportProgress(responseData.progress));
          dispatch(setExportStatus(responseData.status));
          await new Promise((resolve) => setTimeout(resolve, 5000));
          return await checkStatus();
        }
      } catch (error) {
        console.error(error);
        throw error;
      }
    };

    return await checkStatus();
  };
};

export const submitExportRequest = (
  fileName: string,
  exportType: ExportFormatOptions,
  exportSource: FilesToExport,
  queryParamsFilters: FiltersObject,
  includeRawData: boolean = false,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    try {
      const state = getState();
      const datasetID = state.singleDataset?.stats?.dataset?.id;
      let params =
        "?file_name=" +
        fileName +
        "&export_format=" +
        exportType.toLowerCase() +
        "&include_images=" +
        includeRawData;
      let url = "";
      let response: AxiosResponse;
      if (exportSource === "SELECTED") {
        const exportRequestData = getExportRequestData(
          state,
          queryParamsFilters,
        );
        const requestBody = {
          media_selection: exportRequestData,
        };
        params =
          params +
          "&" +
          getCurrentFilterQueries(getState(), queryParamsFilters, false).split(
            "?",
          )[1];
        url = composeApiURL(
          process.env.REACT_APP_API_ENDPOINT!,
          `/dataset/${datasetID}/export_entities_async${params}`,
        );
        response = await axios({
          method: "POST",
          url: url,
          data: requestBody,
        });
      } else {
        if (exportSource === "FILTERED") {
          params =
            params +
            "&" +
            getCurrentFilterQueries(
              getState(),
              queryParamsFilters,
              false,
            ).split("?")[1];

          const clusterId = getNavigationClusterID(getState());
          params += !!clusterId ? `&cluster_id=${clusterId}` : "";
        }
        url = composeApiURL(
          process.env.REACT_APP_API_ENDPOINT!,
          `/dataset/${datasetID}/export_context_async${params}`,
        );
        response = await axios({
          method: "GET",
          url: url,
        });
      }
      const responseData = response.data;

      if (!responseData) {
        throw new Error(FALLBACK_EXPORT_ERROR_TEXT);
      } else if (
        responseData.status === "REJECTED" ||
        responseData.status === "FAILED"
      ) {
        throw new Error(
          responseData.result_message || FALLBACK_EXPORT_ERROR_TEXT,
        );
      } else if (responseData.id) {
        try {
          const downloadUri = await dispatch(
            fetchExportStatus(responseData.id, datasetID) as any,
          );

          downloadFile(`${fileName}.zip`, { type: "href", href: downloadUri });

          return downloadUri;
        } catch (error) {
          throw error;
        }
      }
    } catch (error) {
      console.error(error);
      throw error;
    }
  };
};

export const setMetadataForData = (total: number, size: number) => {
  let data = { total, size };
  return { type: SET_PAGINATION_METADATA_FOR_DATA, data };
};

export const setMetadataForCluster = (total: number, size: number) => {
  let data = { total, size };
  return { type: SET_PAGINATION_METADATA_FOR_CLUSTER, data };
};

export const fetchSimilarityThresholdsInfo = (
  datasetId: string,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch) => {
    const path = `/explore/${datasetId}/exploration_metadata_get_thresholds`;
    try {
      const url = composeApiURL(process.env.REACT_APP_API_ENDPOINT!, path);
      const response = await axios.get(url);
      const thresholds = response.data.similarity_thresholds;
      if (thresholds && thresholds.length > 0) {
        dispatch(setSimilarityThresholds(response.data));
      }
    } catch (error) {
      console.log(error);
    }
  };
};

export const fetchDataPagePagination = (
  datasetID: string,
  queryParamsFilters: FiltersObject,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    const state = getState();
    const basePath = `/explore/${datasetID}/exploration_metadata`;
    const queries = getCurrentFilterQueries(state, queryParamsFilters);
    const path = `${basePath}${queries}`;
    try {
      const url = composeApiURL(process.env.REACT_APP_API_ENDPOINT!, path);
      const { data } = await axios.get(url);
      const totalItems = queryParamsFilters.flat_response
        ? data.items
        : data.cluster_count;
      const pageSize = data.page_size;
      dispatch(setMetadataForData(totalItems, pageSize));
    } catch (error) {
      console.log(error);
      dispatch(setFetchError(true));
      dispatch(setCardslistLoading(false));
    }
  };
};

export const fetchClusterPagePagination = (
  datasetID: string,
  clusterID: string,
  queryParamsFilters: FiltersObject,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    const state = getState();
    const basePath = `/explore/${datasetID}/similarity_cluster/${clusterID}/cluster_metadata`;
    const queries = getCurrentFilterQueries(state, queryParamsFilters);
    const path = `${basePath}${queries}`;
    try {
      const url = composeApiURL(process.env.REACT_APP_API_ENDPOINT!, path);
      const response = await axios.get(url);
      const totalItems = response.data.cluster_size;
      const pageSize = response.data.page_size;
      dispatch(setMetadataForCluster(totalItems, pageSize));
    } catch (error) {
      console.log(error);
      dispatch(setFetchError(true));
      dispatch(setNavigationClusterLoader(false));
    }
  };
};

export const fetchDatasetTags = (
  datasetId: any,
  entityType: EntityType = "IMAGES",
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch) => {
    try {
      const path = `/dataset/${datasetId}/tags?entity_type=${entityType}`;
      const url = composeApiURL(process.env.REACT_APP_API_ENDPOINT!, path);

      const response = await axios.get(url);
      if (!!response.data && response.data.length > 0) {
        let uniqueTagsArray = response.data
          .filter(
            (tag: any, index: number, self: any[]) =>
              index === self.findIndex((t) => t.id === tag.id),
          )
          .map((tag: any) => ({ id: tag.id, text: tag.name }));
        dispatch(setDatasetTags(uniqueTagsArray));
        uniqueTagsArray = [
          { id: "untagged", text: "untagged" },
          ...uniqueTagsArray.filter(
            (tag: any) => tag.text.toLowerCase() !== "untagged",
          ),
        ];
        dispatch(setUserTags(uniqueTagsArray));
      } else {
        const untaggedTag = {
          id: "untagged",
          text: "untagged",
        };

        // @ts-ignore
        dispatch(setUserTags([untaggedTag]));
      }
    } catch (error) {
      console.error(error);
    }
  };
};

export const fetchContextualDatasetTags = (
  datasetId: any,
  queryParamsFilters: FiltersObject,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState: () => State) => {
    const isExplorationPerformanceEnabled =
      getIsExplorationPerformanceEnabled(getState());
    try {
      const queryParams = getCurrentFilterQueries(
        getState(),
        queryParamsFilters,
        false,
        true,
      );
      const path = `/dataset/${datasetId}/tags${queryParams}`;
      const url = composeApiURL(process.env.REACT_APP_API_ENDPOINT!, path);

      const response = await axios.get(url);
      if (!!response.data && response.data.length > 0) {
        let uniqueTagsArray = response.data
          .filter(
            (tag: any, index: number, self: any[]) =>
              index === self.findIndex((t) => t.id === tag.id),
          )
          .map((tag: any) => ({ id: tag.id, text: tag.name }));
        const uniqueTagData = uniqueTagsArray;
        uniqueTagsArray = [
          { id: "untagged", text: "untagged" },
          ...uniqueTagsArray.filter(
            (tag: any) => tag.text.toLowerCase() !== "untagged",
          ),
        ];
        if (isExplorationPerformanceEnabled) {
          dispatch(setDatasetTags(uniqueTagData));
          dispatch(setUserTags(uniqueTagsArray));
        }
        dispatch(setContextualUserTags(uniqueTagsArray));
      } else {
        const untaggedTag = {
          id: "untagged",
          text: "untagged",
        };

        if (isExplorationPerformanceEnabled) {
          // @ts-ignore
          dispatch(setUserTags([untaggedTag]));
        }

        // @ts-ignore
        dispatch(setContextualUserTags([untaggedTag]));
      }
    } catch (error) {
      console.error(error);
    }
  };
};

export const fetchUserTags = (
  datasetId: any,
  queryParamsFilters: FiltersObject,
  clusterId?: any,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    dispatch(setUserTagLoading(true));

    const queryParams = getCurrentFilterQueries(
      getState(),
      queryParamsFilters,
      false,
    );

    try {
      let path: any;
      if (clusterId) {
        path = `/dataset/${datasetId}/cluster/${clusterId}/tags${queryParams}`;
      } else {
        path = `/dataset/${datasetId}/tags${queryParams}`;
      }
      const url = composeApiURL(process.env.REACT_APP_API_ENDPOINT!, path);

      const response = await axios.get(url);
      if (!!response.data && response.data.length > 0) {
        let newUserTags = response.data
          ?.filter(
            (tag: any, index: number, self: any[]) =>
              index === self.findIndex((t) => t.id === tag.id),
          )
          .map((tag: any) => {
            return {
              id: tag.id,
              text: tag.name,
            };
          });
        newUserTags = [
          { id: "untagged", text: "untagged" },
          ...newUserTags.filter(
            (tag: any) => tag.text.toLowerCase() !== "untagged",
          ),
        ];

        dispatch(setUserTags(newUserTags));
      } else {
        const untaggedTag = {
          id: "untagged",
          text: "untagged",
        };

        // @ts-ignore
        dispatch(setUserTags([untaggedTag]));
      }
      dispatch(setUserTagLoading(false));
    } catch (error) {
      console.error(error);
      dispatch(setUserTagLoading(false));
      dispatch(setCardslistLoading(false));
    }
  };
};

export const updateUserTags = (
  newTag: string,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch) => {
    try {
      const url = composeApiURL(process.env.REACT_APP_API_ENDPOINT!, `/tags`);
      const response = await axios.post(url, { tag_name: newTag });
      let newTagID = response.data.tag_id;
      dispatch(updateUserTagsWithNewTags([{ id: newTagID, text: newTag }]));
      if (newTagID) {
        dispatch(addImageTag(newTagID, newTag) as any);
      }
    } catch (error) {
      console.error(error);
    }
  };
};

export const addNewTagFromCart = (
  datasetID: string,
  tagName: string,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    try {
      const url = composeApiURL(process.env.REACT_APP_API_ENDPOINT!, `/tags`);
      const response = await axios.post(url, { tag_name: tagName });
      const newTagID = response.data.tag_id;

      if (newTagID) {
        const newTag = { id: newTagID, text: tagName };
        await Promise.all([
          dispatch(updateUserTagsWithNewTags([newTag])),
          dispatch(updateExportTags(datasetID, newTag) as any),
          dispatch(
            setDatasetTags([...getState().singleDataset.datasetTags, newTag]),
          ),
          dispatch(
            setContextualUserTags([
              ...getState().singleDataset.contextualUserTags,
              newTag,
            ]),
          ),
        ]);

        return newTagID;
      }
    } catch (error) {
      console.error(error);
    }
  };
};

export const resetStore = () => {
  return { type: RESET_STORE };
};

export const setNavigationContext = (
  currentPage: string,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    let navContext = [...getState().singleDataset.navigationContext];
    if (
      !navContext.includes(currentPage) &&
      [DATA_PAGE, CLUSTER_DETAILS_PAGE, IMAGE_DETAILS_PAGE].includes(
        currentPage,
      )
    ) {
      const newContext = [...navContext, currentPage];
      dispatch({ type: SET_NAVIGATION_CONTEXT, data: newContext });
    }
  };
};

export const setIsImageUploading = (data: boolean) => {
  return { type: SET_IS_IMAGE_UPLOADING, data };
};

export const setHoveredFileState = (
  data: {
    path: string;
    top: number;
    right: number;
  } | null,
) => {
  return { type: HOVERED_FILE_STATE, data };
};

export const setFetchError = (data: boolean) => {
  return { type: SET_FETCH_ERROR, data };
};

export const setAutocompleteSuggestions = (data: string[]) => {
  return { type: SET_AUTOCOMPLETE_SUGGESTIONS, data };
};

export const setAutocompleteLoading = (data: boolean) => {
  return { type: SET_AUTOCOMPLETE_LOADING, data };
};

export const setAutocompleteNoResultsFlag = (data: boolean) => {
  return { type: SET_AUTOCOMPLETE_NO_RESULTS_FLAG, data };
};

export const fetchAutocompleteSuggestions = (
  datasetId: string,
  query: string,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch) => {
    try {
      const encodedQuery = encodeURIComponent(query);
      const url = composeApiURL(
        process.env.REACT_APP_API_ENDPOINT!,
        `/dataset/${datasetId}/autocomplete?query=${encodedQuery}`,
      );

      const response = await axios.get(url);
      const suggestions = response.data.results || [];

      if (suggestions.length > 0) {
        dispatch(setAutocompleteSuggestions(suggestions));
        dispatch(setAutocompleteNoResultsFlag(false));
      } else {
        dispatch(setAutocompleteSuggestions([]));
        dispatch(setAutocompleteNoResultsFlag(true));
      }
      dispatch(setAutocompleteLoading(false));
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      dispatch(setAutocompleteSuggestions([]));
      dispatch(setAutocompleteLoading(false));
      dispatch(setAutocompleteNoResultsFlag(false));
    }
  };
};

export const setRecentSearches = (data: any) => {
  localStorage.setItem("recent_searches", JSON.stringify(data));
  return { type: SET_RECENT_SEARCHES, data };
};

export const addToRecentSearches = (
  datasetId: string,
  text: string,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    try {
      const state = getState();
      const recentSearches = getRecentSearches(state);
      let newValue = {};

      if (Object.keys(recentSearches).length > 0) {
        if (datasetId in recentSearches) {
          const existingValues = [...recentSearches[datasetId]];

          const existingIndex = existingValues.indexOf(text);
          if (existingIndex !== -1) existingValues.splice(existingIndex, 1);

          const newArr = [text, ...existingValues].slice(0, 5);
          newValue = { ...recentSearches, [datasetId]: newArr };
        } else {
          newValue = { ...recentSearches, [datasetId]: [text] };
        }
      } else {
        newValue = { [datasetId]: [text] };
      }

      dispatch(setRecentSearches(newValue));
    } catch (error) {
      console.error("Error occoured while adding to recent searches:", error);
    }
  };
};

export const removeFromRecentSearches = (
  datasetId: string,
  text: string,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    try {
      const state = getState();
      const recentSearches = getRecentSearches(state);
      let newValue = {};

      let existingValues = [...recentSearches[datasetId]];
      const existingIndex = existingValues.indexOf(text);
      if (existingIndex !== -1) existingValues.splice(existingIndex, 1);

      newValue = { ...recentSearches, [datasetId]: [...existingValues] };
      dispatch(setRecentSearches(newValue));
    } catch (error) {
      console.error("Error occoured while adding to recent searches:", error);
    }
  };
};

export const setImagePageParamsContext = (data: {
  imageId: string;
  searchRegion: string;
}) => {
  return { type: SET_IMAGE_PAGE_PARAMS_CONTEXT, data };
};

export const clearImagePageParamsContext = () => {
  const data = { imageId: "", searchRegion: "" };
  return { type: SET_IMAGE_PAGE_PARAMS_CONTEXT, data };
};

export const setAssignedTags = (data: FilterOption[]) => {
  return { type: SET_ASSIGNED_TAGS, data };
};

export const setTemporaryAssignedTags = (data: FilterOption[]) => {
  return { type: SET_TEMPORARY_ASSIGNED_TAGS, data };
};

export const fetchAssignedTags = (
  datasetId: string,
  exportRequestData: any,
  assignedTags: FilterOption[],
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch) => {
    try {
      const url = composeApiURL(
        process.env.REACT_APP_API_ENDPOINT!,
        `/dataset/${datasetId}/tags/union`,
      );

      const response = await axios.post(url, {
        tags_retrieval: exportRequestData,
      });
      const responseTags = response.data;

      if (responseTags.length > 0) {
        const structuredTags = responseTags.map(
          (tag: { id: string; name: string }) => {
            // Check if the fetched tag ID exists in the previously assigned tags
            const previouslyAssignedTag = assignedTags.find(
              (assignedTag: any) => assignedTag.id === tag.id,
            );

            if (previouslyAssignedTag) {
              return { ...previouslyAssignedTag, text: tag.name };
            } else {
              return { id: tag.id, text: tag.name, selected: true };
            }
          },
        );

        dispatch(setAssignedTags(structuredTags));
      } else {
        dispatch(setAssignedTags([]));
      }
    } catch (error) {
      console.error("Error fetching assigned tags:", error);
    }
  };
};

export const fetchTemporaryAssignedTags = (
  datasetId: string,
  exportRequestData: any,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    try {
      const cardTags = getSelectedCardTags(getState());
      const url = composeApiURL(
        process.env.REACT_APP_API_ENDPOINT!,
        `/dataset/${datasetId}/tags/union`,
      );
      const response = await axios.post(url, {
        tags_retrieval: exportRequestData,
      });
      const responseTags = response.data;

      if (responseTags.length > 0) {
        const structuredTags: FilterOption[] = responseTags.map(
          (tag: { id: string; name: string }) => {
            return {
              id: tag.id,
              text: tag.name,
              selected: cardTags.has(tag.name),
            };
          },
        );

        dispatch(setTemporaryAssignedTags(structuredTags));
      } else {
        dispatch(setTemporaryAssignedTags([]));
      }
    } catch (error) {
      console.error("Error fetching assigned tags:", error);
    }
  };
};

export const submitRemoveTagsRequest = (
  datasetID: string,
  requestBody: any,
  queryParamsFilters: FiltersObject,
  clusterID?: string,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch) => {
    return new Promise<void>(async (resolve, reject) => {
      try {
        const path = `/dataset/${datasetID}/tags/remove`;
        const url = composeApiURL(process.env.REACT_APP_API_ENDPOINT!, path);
        axios
          .patch(url, { tags_removal: requestBody })
          .then(() => {
            dispatch(
              fetchCardDataOnTagUpdate(
                datasetID,
                queryParamsFilters,
                clusterID,
              ) as any,
            );
            resolve();
          })
          .catch((error) => reject(error));
      } catch (error) {
        console.log(error);
        reject(error);
      }
    });
  };
};

export const submitShareInviteRequest = (
  datasetID: string,
  email: string,
): ThunkAction<void, State, unknown, any> => {
  return async () => {
    try {
      const path = `/manage/${datasetID}/invite`;
      const url = composeApiURL(process.env.REACT_APP_API_ENDPOINT!, path);
      const formData = new FormData();
      formData.append("grant_to_user_email", email.toLowerCase());

      const response = await axios.put(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return error.response;
      }
    }
  };
};

export const submitShareViewInviteRequest = (
  datasetID: string,
  viewID: string,
  email: string,
  description: string = "",
): ThunkAction<void, State, unknown, any> => {
  return async () => {
    try {
      const path = `/manage/${datasetID}/${viewID}/invite`;
      const url = composeApiURL(process.env.REACT_APP_API_ENDPOINT!, path);
      const formData = new FormData();
      formData.append("grant_to_user_email", email);
      if (description) {
        formData.append("description", description);
      }
      const response = await axios.put(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return error.response;
      }
    }
  };
};

export const submitRevokeAccessRequest = (
  datasetID: string,
  userId: string,
): ThunkAction<void, State, unknown, any> => {
  return async () => {
    try {
      const path = `/manage/${datasetID}/share/${userId}`;
      const url = composeApiURL(process.env.REACT_APP_API_ENDPOINT!, path);
      const response = await axios.delete(url);
      return response;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return error.response;
      }
    }
  };
};

export const submitRevokeInvitationRequest = (
  datasetID: string,
  email: string,
): ThunkAction<void, State, unknown, any> => {
  return async () => {
    try {
      const encodedEmail = encodeURIComponent(email);
      const path = `/manage/${datasetID}/invite/${encodedEmail}`;
      const url = composeApiURL(process.env.REACT_APP_API_ENDPOINT!, path);
      const response = await axios.delete(url);
      return response;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return error.response;
      }
    }
  };
};

export const fetchUsersWithAccess = (
  datasetID: string,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch) => {
    try {
      const path = `/manage/${datasetID}/share`;
      const url = composeApiURL(process.env.REACT_APP_API_ENDPOINT!, path);
      const response = await axios.get(url);
      if (response.data && response.data.members) {
        dispatch(setUsersWithAccess(response.data.members));
      }
      if (response.data && response.data.invitations) {
        dispatch(setInvitedEmails(response.data.invitations));
      }
    } catch (error) {
      console.log(error);
    }
  };
};

export const acceptInvitation = (
  datasetID: string,
  invitationToken: string,
): ThunkAction<void, State, unknown, any> => {
  return async () => {
    try {
      const path = `/manage/${datasetID}/share`;
      const url = composeApiURL(process.env.REACT_APP_API_ENDPOINT!, path);
      const formData = new FormData();
      formData.append("invite_token", invitationToken);
      const response = await axios.put(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response;
    } catch (error) {
      console.log(error);
    }
  };
};

export const fetchMetadataSources = (
  datasetId: string,
  entityTypeValue: EntityType,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    try {
      dispatch(setMetadataSourcesLoading(true));
      const path = `/explore/${datasetId}/metadata_sources?entity_type=${entityTypeValue}`;
      const url = composeApiURL(process.env.REACT_APP_API_ENDPOINT!, path);
      const response = await axios.get(url);

      if (!response.data.length) {
        dispatch(setDatasetEnrichmentModels([]));
        dispatch(setDatasetExternalSources([]));
      } else {
        // Process the response and dispatch data accordingly
        const { enrichmentModels, externalSources } = response.data.reduce(
          (acc: any, source: any) => {
            let filterItem: Omit<MetadataSource, "selected"> = {
              count: 0,
              id: source.id,
              title: source.name,
              iconPath: source.vendor_logo_uri,
              isSelectable: source.filter_by_source_id,
            };

            if (source.metadata_source_type === "MODEL") {
              acc.enrichmentModels.push(filterItem);
            } else if (source.metadata_source_type === "EXTERNAL_SOURCE") {
              acc.externalSources.push(filterItem);
            }

            return acc;
          },
          {
            enrichmentModels: [] as MetadataSource[],
            externalSources: [] as MetadataSource[],
          },
        );

        dispatch(setDatasetEnrichmentModels(enrichmentModels));
        dispatch(setDatasetExternalSources(externalSources));
      }
    } catch (error) {
      console.error("Error fetching metadata sources:", error);
    } finally {
      dispatch(setMetadataSourcesLoading(false));
    }
  };
};

export const checkForResultsInMislabels = (
  queryParamsFilters: FiltersObject,
  urlParams: URLSearchParams,
  datasetId?: string,
  clusterId?: string,
): ThunkAction<Promise<boolean>, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    const state = getState();
    const basePath = clusterId
      ? `/explore/${datasetId}/similarity_cluster/${clusterId}/cluster_metadata`
      : `/explore/${datasetId}/exploration_metadata`;

    const queries = new URLSearchParams(
      getCurrentFilterQueries(state, queryParamsFilters),
    );

    // Override query values with urlSearchParams
    if (urlParams.has(ISSUE_TYPE_PARAMETER)) {
      queries.set("issues", urlParams.get(ISSUE_TYPE_PARAMETER)!);
    }
    if (urlParams.has(ISSUE_CONFIDENCE)) {
      queries.set(ISSUE_CONFIDENCE, urlParams.get(ISSUE_CONFIDENCE)!);
    }

    const path = `${basePath}?${queries.toString()}`;

    try {
      const url = composeApiURL(process.env.REACT_APP_API_ENDPOINT!, path);
      const response = await axios.get(url);

      const count = clusterId
        ? response.data?.cluster_size
        : response.data?.cluster_count;

      return count < 1;
    } catch (error) {
      console.error("Error fetching mislabels:", error);
      dispatch(setFetchError(true));
      return false;
    }
  };
};

// Add all clusters to temporary export data
export const addAllForTempExport = (
  clusters: any[],
  allSelectedFilters: any,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    try {
      const state = getState();
      const temporaryData = state.singleDataset.temporaryExportData;
      const selectedClusters = clusters.map((cluster) => {
        if (cluster.flat_response) {
          return {
            ...cluster.previews[0],
            cluster_id: cluster.cluster_id,
            exportType: "media",
            cluster_filters: allSelectedFilters,
          };
        }
        return {
          ...cluster,
          exportType: "cluster",
          cluster_filters: allSelectedFilters,
        };
      });
      let newTemporaryData = { ...temporaryData };
      if (newTemporaryData) {
        const existingClusters = newTemporaryData.exportDataArray || [];
        // Merge new clusters with the existing clusters (avoiding duplicates based on ID)
        const updatedClusterArray = [
          ...existingClusters.filter(
            (existingCluster: { cluster_id: string }) => {
              return !selectedClusters.some((newCluster) => {
                return newCluster.cluster_id === existingCluster.cluster_id;
              });
            },
          ),
          ...selectedClusters,
        ];
        newTemporaryData = {
          ...newTemporaryData,
          exportDataArray: updatedClusterArray,
        };
      } else {
        newTemporaryData = { tags: [], exportDataArray: selectedClusters };
      }
      dispatch(setTemporaryExportData(newTemporaryData));
    } catch (error) {
      console.error("Error in adding clusters for temp export:", error);
    }
  };
};

// Add all media IDs from cluster page to temporary export data
export const addAllMediaIDsFromClusterPageToTemp = (
  datasetID: string,
  allSelectedFilters: any,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    const state = getState();
    const temporaryData = state.singleDataset.temporaryExportData;
    const navigationCluster = { ...state.singleDataset.navigationCluster };
    let newTemporaryData = { ...temporaryData };
    try {
      const exportDataArray = newTemporaryData?.exportDataArray || [];
      const tagsArray = newTemporaryData?.tags || [];
      const mediaArray = navigationCluster.previews.map(
        (singlePreview: any) => {
          return {
            ...singlePreview,
            exportType: "media",
            cluster_id: navigationCluster.cluster_id,
            cluster_filters: allSelectedFilters,
          };
        },
      );

      newTemporaryData = {
        exportDataArray: [...exportDataArray, ...mediaArray],
        tags: tagsArray,
      };

      dispatch(setTemporaryExportData(newTemporaryData));
    } catch (error) {
      console.error("Error adding all media IDs to temp export:", error);
    }
  };
};

/**
 * Shared action for comprehensive dataset data refresh (EXACT copy of DataPage's fetchForAsyncDataPage)
 * This ensures the UI updates with all the latest dataset information after operations like reindexing
 */
export const fetchForAsyncDataPage = (
  datasetID: string,
  queryParamsFilters: FiltersObject,
): ThunkAction<void, State, unknown, any> => {
  return async (dispatch: Dispatch, getState) => {
    const isExplorationPerformanceEnabled =
      getIsExplorationPerformanceEnabled(getState());
    const entityParam = queryParamsFilters[ENTITY_TYPE_PARAMETER];
    const entityTypeValue = isValidEntityType(entityParam)
      ? entityParam
      : "IMAGES";

    // fetchRightPanelData equivalent from DataPage
    const fetchRightPanelData = async () => {
      if (!isExplorationPerformanceEnabled) {
        return;
      }
      await dispatch(fetchMetadataSources(datasetID, entityTypeValue) as any);
    };

    dispatch(setCardslistLoading(true));
    dispatch(setSimilarityData(null));
    await dispatch(fetchDataset(datasetID) as any);

    await dispatch(
      fetchDataPagePagination(datasetID, queryParamsFilters) as any,
    );
    await dispatch(fetchSimilarityData(datasetID, queryParamsFilters) as any);
    dispatch(setCardslistLoading(false));

    await dispatch(fetchDatasetIssues(datasetID, queryParamsFilters) as any);
    datasetID &&
      (await dispatch(
        fetchDataMetadataSummary(datasetID, queryParamsFilters) as any,
      ));
    await dispatch(
      fetchDatasetContextualLabels(datasetID, queryParamsFilters) as any,
    );
    if (isExplorationPerformanceEnabled) {
      await fetchRightPanelData();
    }
  };
};
